import { createContext, useContext } from 'react';

/**
 * Pure role-model module — types, constants, persistence helper, and the
 * `useRole` hook. Split out from `roleContext.tsx` at the start of Sprint 2 so
 * the provider file can export only React components and satisfy
 * `react-refresh/only-export-components` (fast-refresh safety).
 *
 * Why a `.ts` file:
 *   - `roleContext.tsx` must only export components for fast-refresh to work
 *     reliably. Hooks, constants, and helpers therefore live here.
 *   - Call sites import `useRole`, `ALL_ROLES`, and `type RoleId` from this
 *     module. They import `RoleProvider` from `roleContext.tsx`.
 *
 * Behaviour is unchanged from Sprint 1 — this is a pure refactor.
 */

export type RoleId =
  | 'president'
  | 'vpOperations'
  | 'financeManager'
  | 'engineeringDirector'
  | 'opsManager'
  | 'contractsAdmin'
  | 'marketingManager'
  | 'hrManager'
  | 'bilingualViewer';

export const ALL_ROLES: readonly RoleId[] = [
  'president',
  'vpOperations',
  'financeManager',
  'engineeringDirector',
  'opsManager',
  'contractsAdmin',
  'marketingManager',
  'hrManager',
  'bilingualViewer',
] as const;

export const DEFAULT_ROLE: RoleId = 'president';
export const LS_KEY = 'ga-erp-role';

export function readInitialRole(): RoleId {
  if (typeof window === 'undefined') return DEFAULT_ROLE;
  const stored = window.localStorage.getItem(LS_KEY);
  if (stored && (ALL_ROLES as readonly string[]).includes(stored)) {
    return stored as RoleId;
  }
  return DEFAULT_ROLE;
}

export interface RoleContextValue {
  role: RoleId;
  setRole: (next: RoleId) => void;
}

/**
 * React context backing the role switcher. The provider lives in
 * `roleContext.tsx`. Call sites use the `useRole` hook below — they do not
 * read this context directly.
 */
export const RoleContext = createContext<RoleContextValue | undefined>(undefined);

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within a RoleProvider');
  return ctx;
}
